import React from 'react';
import { Modal, Button, Input, Textarea } from '../../ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

// ===============================
// INTERFACES GENÉRICAS
// ===============================

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'number' | 'select' | 'textarea' | 'checkbox' | 'date';
  placeholder?: string;
  required?: boolean;
  options?: { value: string | number; label: string }[];
  validation?: z.ZodTypeAny;
  description?: string;
  rows?: number; // Para textarea
  min?: number; // Para number
  max?: number; // Para number
  step?: number; // Para number
}

export interface CrudFormProps<T> {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: T) => void;
  fields: FormField[];
  title: string;
  submitText?: string;
  isLoading?: boolean;
  initialData?: Partial<T>;
  mode: 'create' | 'edit';
}

// ===============================
// FUNCIÓN PARA CREAR SCHEMA
// ===============================

const createValidationSchema = (fields: FormField[]) => {
  const schemaObject: Record<string, z.ZodTypeAny> = {};
  
  fields.forEach((field) => {
    let validator: z.ZodTypeAny;
    
    switch (field.type) {
      case 'email':
        validator = z.string().email('Email inválido');
        break;
      case 'number':
        validator = z.coerce.number();
        if (field.min !== undefined) validator = (validator as z.ZodNumber).min(field.min);
        if (field.max !== undefined) validator = (validator as z.ZodNumber).max(field.max);
        break;
      case 'checkbox':
        validator = z.boolean();
        break;
      case 'date':
        validator = z.string().refine((val) => val === '' || !isNaN(Date.parse(val)), {
          message: 'Fecha inválida',
        });
        break;
      default:
        validator = z.string();
    }
    
    if (field.required) {
      validator = validator.refine((val) => val !== '' && val !== null && val !== undefined, {
        message: `${field.label} es requerido`,
      });
    } else {
      validator = validator.optional();
    }
    
    // Aplicar validación personalizada si existe
    if (field.validation) {
      validator = field.validation;
    }
    
    schemaObject[field.name] = validator;
  });
  
  return z.object(schemaObject);
};

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export function CrudForm<T extends Record<string, any>>({
  isOpen,
  onClose,
  onSubmit,
  fields,
  title,
  submitText,
  isLoading = false,
  initialData = {},
}: CrudFormProps<T>) {
  const { t } = useTranslation();
  const schema = createValidationSchema(fields);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<any>({
    resolver: zodResolver(schema),
  });

  // Reset form cuando cambian los datos iniciales
  React.useEffect(() => {
    if (initialData) {
      Object.keys(initialData).forEach((key) => {
        const value = initialData[key as keyof T];
        if (value !== undefined) {
          // Convertir fechas ISO a formato YYYY-MM-DD para inputs date
          const field = fields.find(f => f.name === key);
          if (field?.type === 'date' && value) {
            const dateStr = value as string;
            // Extraer solo la parte de fecha (YYYY-MM-DD) del datetime ISO
            const dateOnly = dateStr.split('T')[0];
            setValue(key as any, dateOnly);
          } else {
            setValue(key as any, value);
          }
        }
      });
    }
  }, [initialData, setValue, fields]);

  // Reset form cuando se cierra
  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleFormSubmit = (data: any) => {
    // Procesar datos antes de enviar
    const processedData = { ...data };
    
    // Procesar campos de fecha
    fields.forEach((field) => {
      if (field.type === 'date') {
        const dateValue = processedData[field.name];
        if (dateValue === '' || dateValue === null || dateValue === undefined) {
          // String vacío o valores falsy -> null
          processedData[field.name] = null;
        } else {
          // Convertir YYYY-MM-DD a YYYY-MM-DDTHH:mm:ss formato ISO
          const date = new Date(dateValue + 'T00:00:00');
          processedData[field.name] = date.toISOString();
        }
      }
    });
    
    onSubmit(processedData as T);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="lg">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((field) => (
            <div
              key={field.name}
              className={field.type === 'textarea' ? 'md:col-span-2' : ''}
            >
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              
              {field.type === 'select' ? (
                <select
                  {...register(field.name as any)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('common.select')}</option>
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <Textarea
                  {...register(field.name as any)}
                  placeholder={field.placeholder}
                  rows={field.rows || 3}
                />
              ) : field.type === 'checkbox' ? (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register(field.name as any)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-600">{field.description}</span>
                </div>
              ) : (
                <Input
                  type={field.type}
                  {...register(field.name as any)}
                  placeholder={field.placeholder}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                />
              )}
              
              {errors[field.name] && (
                <p className="text-red-500 text-xs mt-1">
                  {(errors[field.name] as any)?.message || t('errors.validation_error')}
                </p>
              )}
              
              {field.description && field.type !== 'checkbox' && (
                <p className="text-gray-500 text-xs mt-1">{field.description}</p>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={isLoading}
          >
            {submitText ?? t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ===============================
// CAMPOS PREDEFINIDOS COMUNES
// ===============================

export const codigoField: FormField = {
  name: 'codigo',
  label: 'Código',
  type: 'text',
  placeholder: 'Ingresa el código único',
  required: true,
  validation: z.string().min(1, 'Código es requerido').max(50, 'Código muy largo'),
};

export const descripcionField: FormField = {
  name: 'descripcion',
  label: 'Descripción',
  type: 'textarea',
  placeholder: 'Ingresa una descripción detallada',
  required: true,
  rows: 3,
  validation: z.string().min(1, 'Descripción es requerida').max(500, 'Descripción muy larga'),
};

export const estadoField: FormField = {
  name: 'estado',
  label: 'Estado',
  type: 'select',
  required: true,
  options: [
    { value: 'activo', label: 'Activo' },
    { value: 'inactivo', label: 'Inactivo' },
  ],
};

export const telefonoField: FormField = {
  name: 'telefono',
  label: 'Teléfono',
  type: 'text',
  placeholder: '+56 9 1234 5678',
  validation: z.string().optional(),
};

export const emailField: FormField = {
  name: 'email',
  label: 'Email',
  type: 'email',
  placeholder: 'usuario@empresa.com',
  validation: z.string().email('Email inválido').optional(),
};

export const ubicacionField: FormField = {
  name: 'ubicacion',
  label: 'Ubicación',
  type: 'text',
  placeholder: 'Región, comuna, dirección',
  validation: z.string().optional(),
};

export const fechaInicioField: FormField = {
  name: 'fecha_inicio',
  label: 'Fecha de Inicio',
  type: 'date',
  validation: z.string().optional(),
};

export const fechaFinField: FormField = {
  name: 'fecha_fin',
  label: 'Fecha de Fin',
  type: 'date',
  validation: z.string().optional(),
};

export const superficieField: FormField = {
  name: 'superficie_ha',
  label: 'Superficie (Ha)',
  type: 'number',
  placeholder: '0.0',
  min: 0,
  step: 0.01,
  validation: z.coerce.number().min(0, 'Superficie debe ser positiva').optional(),
};

export const propietarioField: FormField = {
  name: 'propietario',
  label: 'Propietario',
  type: 'text',
  placeholder: 'Nombre del propietario',
  validation: z.string().optional(),
};

export const especieField: FormField = {
  name: 'especie',
  label: 'Especie',
  type: 'text',
  placeholder: 'Pino radiata, Eucalipto, etc.',
  validation: z.string().optional(),
};

export const edadField: FormField = {
  name: 'edad_anos',
  label: 'Edad (Años)',
  type: 'number',
  placeholder: '0',
  min: 0,
  max: 100,
  validation: z.coerce.number().min(0).max(100).optional(),
};

export const unidadMedidaField: FormField = {
  name: 'unidad_medida',
  label: 'Unidad de Medida',
  type: 'select',
  required: true,
  options: [
    { value: 'litros', label: 'Litros' },
    { value: 'kg', label: 'Kilogramos' },
    { value: 'ton', label: 'Toneladas' },
    { value: 'unidad', label: 'Unidad' },
    { value: 'metro', label: 'Metro' },
    { value: 'm2', label: 'Metro cuadrado' },
    { value: 'm3', label: 'Metro cúbico' },
  ],
};

export const categoriaField: FormField = {
  name: 'categoria',
  label: 'Categoría',
  type: 'text',
  placeholder: 'Combustible, Lubricante, Herramienta, etc.',
  validation: z.string().optional(),
};

export const precioField: FormField = {
  name: 'precio_unitario',
  label: 'Precio Unitario ($)',
  type: 'number',
  placeholder: '0',
  min: 0,
  step: 0.01,
  validation: z.coerce.number().min(0, 'Precio debe ser positivo').optional(),
};

export const tipoVerificacionField: FormField = {
  name: 'tipo_verificacion',
  label: 'Tipo de Verificación',
  type: 'select',
  required: true,
  options: [
    { value: 'visual', label: 'Visual' },
    { value: 'medicion', label: 'Medición' },
    { value: 'funcionamiento', label: 'Funcionamiento' },
  ],
};

export const frecuenciaHorasField: FormField = {
  name: 'frecuencia_horas',
  label: 'Frecuencia (Horas)',
  type: 'number',
  placeholder: '0',
  min: 0,
  description: 'Cada cuántas horas se debe realizar el chequeo',
  validation: z.coerce.number().min(0).optional(),
};

export const esObligatorioField: FormField = {
  name: 'es_obligatorio',
  label: 'Es Obligatorio',
  type: 'checkbox',
  description: 'Este chequeo es obligatorio para todos los reportes',
  validation: z.boolean(),
};

export default CrudForm;
